// Compliance packs, auditor access and self-assessment (/api/compliance,
// /api/auditor). Shapes mirror HHNodeServer complianceService.buildReport.

export type SectionKey = 'spray' | 'people' | 'whs' | 'harvest';

export interface SectionOption {
  key: SectionKey;
  label: string;
}

export interface StandardSummary {
  key: string;
  name: string;
  note: string;
  checkpoint_count: number;
}

export interface ComplianceAccess {
  sections: SectionOption[];
  all_sections: SectionOption[];
  standards: StandardSummary[];
  can_raise_actions: boolean;
  default_period: { start: string; end: string };
  max_access_days: number;
}

export interface WeatherReading {
  temp_c: number | null;
  wind_kph: number | null;
  wind_dir: string | null;
  humidity: number | null;
  conditions: string | null;
  source: 'station' | 'open_meteo' | null;
}

export type CredentialOnDay = 'current' | 'not_current' | 'none';

export interface ReportSpray {
  id: number;
  reference_number: string | null;
  title: string;
  status: string;
  applied_date: string;
  operator: string | null;
  operator_credential: CredentialOnDay;
  signed_off_by: string | null;
  equipment: string | null;
  prime_mover: string | null;
  calibration: { date: string; output_lpha: number | null; method: string | null } | null;
  targets: { target_type: string; name: string; area_ha: number | null }[];
  mix: {
    product_name: string;
    apvma_number: string | null;
    moa_group: string | null;
    rate: number | null;
    rate_unit: string | null;
    target_pest: string | null;
    whp_days: number | null;
    is_primary: boolean;
  }[];
  batches: { product_name: string; batch_number: string; expiry_date: string | null }[];
  total_water_l: number | null;
  actual_area_ha: number | null;
  actual_area_source: string | null;
  total_target_area_ha: number | null;
  weather_start: WeatherReading | null;
  weather_end: WeatherReading | null;
  whp_days: number | null;
  whp_clear_date: string | null;
  whp_override_status: string | null;
  whp_override_decided_by: string | null;
  label_compliance: string | null;
  /** A quarantine treatment of a load (ICA plan). Absent from older reports. */
  quarantine?: { code: string; load_number: string; validated_at: string | null; validated_by: string | null } | null;
}

export interface ReportCredential {
  type: string;
  number: string | null;
  issuer: string | null;
  issued_date: string | null;
  expiry_date: string | null;
  status_at_end: 'current' | 'expired';
}

export interface SpraySection {
  applications: ReportSpray[];
  cancelled_count: number;
  operators: { name: string; sprays: number; sprays_without_current_credential: number; credentials: ReportCredential[] }[];
  chemical_stock: {
    product_name: string; apvma_number: string | null; unit: string; opening: number; received: number;
    used: number; adjusted: number; disposed: number; closing: number; last_stocktake: string | null;
  }[];
  calibrations: {
    equipment: string; equipment_type: string | null; sprays_in_period: number; calibrations_in_period: number;
    last_calibration: { date: string; method: string | null; output_lpha: number | null; by: string | null } | null;
  }[];
  whp_harvest: {
    areas: {
      area_name: string; block_name: string | null; farm_name: string | null; cultivar_name: string | null;
      first_pick: string; last_pick: string; bins: number; bins_within_whp: number;
      conflicts: { reference_number: string | null; title: string; applied: string; whp_clear_date: string }[];
      latest_clear_before_first_pick: string | null;
    }[];
    bins_without_planting: number;
  };
}

export interface PeopleSection {
  inductions: {
    person: string; induction: string; basis: string; signed_off: string; signed_off_by: string | null;
    expires_on: string | null; status_at_end: 'current' | 'expired';
  }[];
  policies: {
    title: string; doc_type: string; reference_code: string | null; version: number; audience: string;
    audience_count: number; acknowledged: number; outstanding: number;
    acknowledgements: { person: string; version: number; current: boolean; method: string; date: string }[];
  }[];
  certifications: (ReportCredential & { person: string; category: string | null })[];
}

export interface WhsSection {
  documents: {
    title: string; doc_type: string; reference_code: string | null; version: string | null; status: string;
    effective_date: string | null; review_date: string | null;
  }[];
  risk_assessments: {
    title: string; reference_code: string | null; assessment_type: string; status: string;
    assessment_date: string | null; review_date: string | null; hazards: number; high_residual: number;
  }[];
  incidents: {
    tracking_code: string; report_type: string; title: string | null; occurred_date: string | null;
    reported_date: string | null; severity: string | null; status: string; is_notifiable: boolean;
    regulator_notified_date: string | null; closed_date: string | null; actions: number;
  }[];
  actions: {
    title: string; source_type: string; action_type: string; priority: string; assigned_to: string | null;
    due_date: string | null; status: string; raised_date: string | null; completed_date: string | null;
    verified_date: string | null; overdue: boolean;
  }[];
  audits: {
    title: string; audit_type: string; standard_ref: string | null; audit_date: string | null; status: string;
    findings: number; open_findings: number;
  }[];
}

export interface HarvestSection {
  bins: { total: number; with_area: number; tagged: number; loaded: number };
  loads: {
    load: string; destination: string | null; status: string; dispatched_date: string | null;
    bins: number; plantings: string | null; packouts: string | null;
  }[];
}

export interface ComplianceReport {
  organisation: string;
  timezone: string;
  period: { start: string; end: string };
  farms: string[] | null;
  sections_included: SectionKey[];
  pseudonymised: boolean;
  generated_at: string;
  last_self_assessment: string | null;
  sections: {
    spray?: SpraySection;
    people?: PeopleSection;
    whs?: WhsSection;
    harvest?: HarvestSection;
  };
}

export type CheckState = 'met' | 'gap' | 'no_data';
export interface CheckResult {
  state: CheckState;
  detail: string;
}
export type AutoChecks = Record<string, CheckResult>;

export type CheckpointStatus = 'unanswered' | 'compliant' | 'non_compliant' | 'not_applicable';

export interface Checkpoint {
  key: string;
  topic: string;
  title: string;
  section: SectionKey | 'manual';
  auto_check: string | null;
  auto: CheckResult | null;
  hidden: boolean;
  status: CheckpointStatus;
  clause_ref: string | null;
  note: string | null;
  evidence_url: string | null;
  updated_at?: string | null;
  updated_by?: string | null;
}

export interface Checklist {
  key: string;
  name: string;
  note: string;
  checkpoints: Checkpoint[];
  self_assessments: {
    id: number;
    completed_at: string;
    completed_by?: string | null;
    summary?: { counts: Record<CheckpointStatus, number> };
    counts?: Record<CheckpointStatus, number> | null;
  }[];
  period?: { start: string; end: string };
}

export interface CompliancePack {
  id: number;
  title: string;
  standard_key: string | null;
  period_start: string;
  period_end: string;
  sections: SectionKey[];
  pseudonymised: boolean;
  content_sha256: string;
  generated_at: string;
  generated_by?: string | null;
}

export interface PackInput {
  title: string;
  standard_key: string | null;
  period_start: string;
  period_end: string;
  farm_ids: number[] | null;
  sections: SectionKey[];
  pseudonymise: boolean;
}

export type EngagementStatus = 'active' | 'scheduled' | 'expired' | 'revoked';

export interface AuditEngagement {
  id: number;
  title: string;
  standard_key: string | null;
  auditor_name: string;
  auditor_email: string;
  auditor_company: string | null;
  period_start: string;
  period_end: string;
  farm_ids: number[] | null;
  sections: SectionKey[];
  pseudonymised: boolean;
  access_from: string;
  access_until: string;
  notes: string | null;
  created_at: string;
  revoked_at: string | null;
  last_seen_at: string | null;
  status: EngagementStatus;
  views?: number;
  findings?: number;
  open_findings?: number;
}

export interface EngagementInput {
  title: string;
  standard_key: string | null;
  auditor_name: string;
  auditor_email: string;
  auditor_company: string | null;
  period_start: string;
  period_end: string;
  farm_ids: number[] | null;
  sections: SectionKey[];
  pseudonymised: boolean;
  access_until: string;
  notes: string | null;
}

export type FindingType = 'nonconformity_major' | 'nonconformity_minor' | 'observation' | 'opportunity' | 'conformity';

export interface AuditFinding {
  id: number;
  clause_ref: string | null;
  section: string | null;
  finding: string;
  finding_type: FindingType;
  status: 'open' | 'closed';
  org_response: string | null;
  responded_at: string | null;
  responded_by?: string | null;
  whs_action_id?: number | null;
  corrective_action_raised?: boolean;
  created_at: string;
}

export interface AccessLogEntry {
  action: string;
  detail: Record<string, unknown> | null;
  ip_address: string | null;
  at: string;
}

export interface EngagementDetail {
  engagement: AuditEngagement;
  log: AccessLogEntry[];
  findings: AuditFinding[];
}

export interface AuditorEngagementInfo {
  organisation: string;
  title: string;
  standard: string | null;
  auditor_name: string;
  auditor_company: string | null;
  period_start: string;
  period_end: string;
  sections: SectionOption[];
  pseudonymised: boolean;
  access_until: string;
}

export interface InvitePreview {
  organisation: string;
  title: string;
  auditor_name: string;
  email_hint: string;
  access_until: string;
  status: EngagementStatus;
}

export const FINDING_TYPE_LABELS: Record<FindingType, string> = {
  nonconformity_major: 'Major non-conformance',
  nonconformity_minor: 'Minor non-conformance',
  observation: 'Observation',
  opportunity: 'Opportunity for improvement',
  conformity: 'Conformity noted',
};
